import { Input, Inputs } from 'src/lib/constants';
import { InputsReducer, InputValue } from 'src/redux/reducers/inputs';
import {
  CurrentBalanceCompany,
  DebtTypeEnum,
  FullDebt,
} from 'src/types/inputs';
import { getLegacyTrackingValue } from 'src/utils/analytics/utils';

export const getQuizAnswers = (inputs: InputsReducer) =>
  Object.values(Input).reduce((acc, inputName) => {
    acc[inputName] = inputs[inputName] as InputValue;
    return acc;
  }, {} as Record<string, any>) as InputsReducer;

const caseInsensitiveFind = (
  input: Input,
  queryValue: string | string[] | undefined,
  inputValue: string | boolean | string[] | boolean[],
  allowLegacyValues = false
): boolean => {
  if (typeof queryValue === 'string') {
    if (typeof inputValue === 'boolean') {
      return queryValue === `${inputValue}`;
    }

    if (typeof inputValue === 'string') {
      return (
        (allowLegacyValues &&
          getLegacyTrackingValue(input, inputValue) === queryValue) ||
        queryValue === inputValue
      );
    }
  }

  return false;
};

const sanitizeFullDebt = (debt: string): FullDebt => {
  const [debtType, institution, amount] = debt.split('-');

  return {
    debtType: debtType as DebtTypeEnum,
    institution: institution as CurrentBalanceCompany,
    amount: parseInt(amount),
  };
};

export const sanitizeInputValue = (
  inputKey: keyof InputsReducer,
  queryValue?: string | string[],
  allowLegacyValues = false
): InputValue => {
  const input = Inputs[inputKey];
  // case to consider numeric values
  if (
    queryValue &&
    !Array.isArray(queryValue) &&
    !isNaN(parseInt(queryValue))
  ) {
    return parseInt(queryValue);
  }

  if (Array.isArray(input)) {
    // Parse fullDebt objects
    if (inputKey === Input.FullDebt) {
      if (Array.isArray(queryValue)) {
        return queryValue.map((debt) => sanitizeFullDebt(debt));
      }
      return !!queryValue ? [sanitizeFullDebt(queryValue!)] : null;
    }

    const matchingValue = Object.values(input[0]).filter((inputValue) => {
      if (!Array.isArray(queryValue)) {
        return caseInsensitiveFind(
          inputKey,
          queryValue,
          inputValue,
          allowLegacyValues
        );
      }
      return queryValue.some((value) =>
        caseInsensitiveFind(inputKey, value, inputValue, allowLegacyValues)
      );
    }) as unknown as string[];

    if (matchingValue.length) {
      return matchingValue as InputValue;
    }
  } else {
    const matchingValue = Object.values(input).find((inputValue) =>
      caseInsensitiveFind(inputKey, queryValue, inputValue, allowLegacyValues)
    );

    if (matchingValue !== undefined) {
      return matchingValue as InputValue;
    }
  }

  return null;
};
